class ModelSwitcher extends HTMLElement {
  constructor() {
    super();
    this.productGrid = document.querySelector(
      `#${this.getAttribute("data-grid")}`
    );
    this.status = this.querySelector(":checked").value;
    this.addEventListener("change", this.handleChangeEvent.bind(this));
    document.addEventListener(
      "modelSwitcher:changed",
      this.handleSwitchChange.bind(this)
    );
  }

  handleSwitchChange(e) {
    if (e.detail.source === this) return;
    if(e.detail.status){
        this.updateInputs(e.detail.status)
    }
  }

  handleChangeEvent(e) {
    let value = e.target.value;
    this.changeClass(value);
  }

  toggleClass() {
    if (this.productGrid) {
      this.status = this.status === "image" ? "model" : "image";
      this.productGrid.classList.toggle(
        "filter--enabled-image",
        this.status === "image"
      );
      this.productGrid.classList.toggle(
        "filter--enabled-model",
        this.status === "model"
      );
    }
  }

  changeClass(type) {
    if (type !== "image" && type !== "model") {
      console.error(`Invalid type "${type}". Use "image" or "model".`);
      return;
    }
    this.status = type;
    document.dispatchEvent(
      new CustomEvent("modelSwitcher:changed", {
        detail: { status: type },
      })
    );
    this.updateClass(type);
  }

  updateClass(type) {
    if (this.productGrid) {
      this.productGrid.classList.toggle(
        "filter--enabled-image",
        type === "image"
      );
      this.productGrid.classList.toggle(
        "filter--enabled-model",
        type === "model"
      );
    }
  }

  updateInputs(type){
    this.querySelectorAll('input[type="radio"]').forEach((radio) => {
        if (radio.value === type){
            radio.checked = true;
        } else{
            radio.checked = false;
        }
    })
  }

  connectedCallback() {}
}

window.customElements.define("model-switcher", ModelSwitcher);

/**
 * Cart drawer closes when FocusTrap treats a click as "outside" the drawer.
 * SmartSize renders its modal in the document outside <cart-drawer>, so closing
 * the size guide counted as an outside click and hid the drawer. Ignore clicks
 * that originate inside SmartSize UI (portals / known hooks).
 */
function isLikelySmartsizeUiTarget(target) {
  if (!target || typeof target.closest !== "function") return false;
  try {
    if (
      target.closest(
        [
          '[class*="smartsize"]',
          '[id*="smartsize"]',
          "[data-smartsize]",
          '[href*="smartsize"]',
        ].join(", ")
      )
    ) {
      return true;
    }
    const name = typeof target.tagName === "string" ? target.tagName.toUpperCase() : "";
    if (name === "IFRAME" && typeof target.getAttribute === "function") {
      const src = target.getAttribute("src") || target.src || "";
      if (/smartsize/i.test(String(src))) return true;
    }
  } catch {
    /* noop */
  }
  return false;
}

customElements.whenDefined("cart-drawer").then(() => {
  const CartDrawerConstructor = window.customElements.get("cart-drawer");
  if (!CartDrawerConstructor || CartDrawerConstructor.__smartsizeDrawerPatchApplied) return;
  CartDrawerConstructor.__smartsizeDrawerPatchApplied = true;

  let proto = CartDrawerConstructor.prototype;
  let inheritedHideForOutsideClickTarget;
  while (proto) {
    if (Object.prototype.hasOwnProperty.call(proto, "hideForOutsideClickTarget")) {
      inheritedHideForOutsideClickTarget = proto.hideForOutsideClickTarget;
      break;
    }
    proto = Object.getPrototypeOf(proto);
  }
  if (!inheritedHideForOutsideClickTarget) return;

  CartDrawerConstructor.prototype.hideForOutsideClickTarget = function (target) {
    if (isLikelySmartsizeUiTarget(target)) return false;
    return inheritedHideForOutsideClickTarget.call(this, target);
  };
});

/**
 * Submit the theme's newsletter forms directly to Klaviyo while preserving
 * the existing theme markup and styling.
 */
const KLAVIYO_NEWSLETTER_FORM_SELECTOR = "form[data-klaviyo-subscribe-form]";
const KLAVIYO_CLIENT_SUBSCRIPTIONS_URL = "https://a.klaviyo.com/client/subscriptions";
const KLAVIYO_API_REVISION = "2026-07-15";

function getKlaviyoPublicApiKey(form) {
  const configuredKey = form.dataset.klaviyoPublicApiKey?.trim();
  if (configuredKey) return configuredKey;

  const scripts = document.querySelectorAll("script[src]");

  for (const script of scripts) {
    const source = script.getAttribute("src") || "";
    if (!/klaviyo/i.test(source)) continue;

    const queryKey = source.match(/[?&]company_id=([a-z0-9]{6})(?:&|$)/i);
    if (queryKey) return queryKey[1];

    const pathKey = source.match(/\/onsite\/js\/([a-z0-9]{6})(?:\/|$)/i);
    if (pathKey) return pathKey[1];
  }

  return "";
}

function setKlaviyoNewsletterStatus(form, type, message) {
  const successMessage = form.querySelector("[data-klaviyo-success]");
  const errorMessage = form.querySelector("[data-klaviyo-error]");

  if (successMessage) successMessage.hidden = type !== "success";
  if (errorMessage) {
    errorMessage.textContent = type === "error" ? message : "";
    errorMessage.hidden = type !== "error";
  }
}

function getKlaviyoErrorDetail(responseBody) {
  return responseBody?.errors?.[0]?.detail || responseBody?.message || "Klaviyo subscription failed";
}

function getKlaviyoNewsletterErrorMessage(form) {
  return form.dataset.klaviyoErrorMessage || "We could not subscribe you right now. Please try again later.";
}

function initializeKlaviyoNewsletterForm(form) {
  if (form.dataset.klaviyoInitialized === "true") return;
  form.dataset.klaviyoInitialized = "true";

  form.addEventListener("submit", async (event) => {
    event.preventDefault();

    const emailInput = form.querySelector('input[type="email"]');
    const submitButton = form.querySelector('button[type="submit"]');
    const fields = form.querySelector("[data-klaviyo-fields]");
    const listId = form.dataset.klaviyoListId?.trim();
    const publicApiKey = getKlaviyoPublicApiKey(form);
    const email = emailInput?.value.trim();

    setKlaviyoNewsletterStatus(form, "idle", "");

    if (!emailInput || !emailInput.checkValidity() || !email) {
      emailInput?.reportValidity();
      return;
    }

    if (!listId || !publicApiKey) {
      console.error("Klaviyo newsletter is missing the list ID or public API key.");
      setKlaviyoNewsletterStatus(
        form,
        "error",
        getKlaviyoNewsletterErrorMessage(form)
      );
      return;
    }

    if (submitButton) submitButton.disabled = true;
    form.setAttribute("aria-busy", "true");

    const payload = {
      data: {
        type: "subscription",
        attributes: {
          custom_source: form.dataset.klaviyoSource || "Shopify newsletter form",
          profile: {
            data: {
              type: "profile",
              attributes: {
                email,
                subscriptions: {
                  email: {
                    marketing: {
                      consent: "SUBSCRIBED",
                    },
                  },
                },
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: "list",
              id: listId,
            },
          },
        },
      },
    };

    try {
      const response = await fetch(
        `${KLAVIYO_CLIENT_SUBSCRIPTIONS_URL}?company_id=${encodeURIComponent(publicApiKey)}`,
        {
          method: "POST",
          headers: {
            Accept: "application/vnd.api+json",
            "Content-Type": "application/vnd.api+json",
            revision: KLAVIYO_API_REVISION,
          },
          body: JSON.stringify(payload),
          credentials: "omit",
        }
      );

      if (!response.ok) {
        const responseBody = await response.json().catch(() => null);
        throw new Error(getKlaviyoErrorDetail(responseBody));
      }

      if (fields) fields.hidden = true;
      setKlaviyoNewsletterStatus(form, "success", "");
    } catch (error) {
      console.error("Klaviyo newsletter subscription failed.", error);
      setKlaviyoNewsletterStatus(
        form,
        "error",
        getKlaviyoNewsletterErrorMessage(form)
      );
    } finally {
      if (submitButton) submitButton.disabled = false;
      form.removeAttribute("aria-busy");
    }
  });
}

function initializeKlaviyoNewsletterForms(root = document) {
  if (root.matches?.(KLAVIYO_NEWSLETTER_FORM_SELECTOR)) {
    initializeKlaviyoNewsletterForm(root);
    return;
  }

  root.querySelectorAll(KLAVIYO_NEWSLETTER_FORM_SELECTOR).forEach(initializeKlaviyoNewsletterForm);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initializeKlaviyoNewsletterForms());
} else {
  initializeKlaviyoNewsletterForms();
}

document.addEventListener("shopify:section:load", (event) => {
  initializeKlaviyoNewsletterForms(event.target);
});

//AB TESTING POSTHOG

//if (posthog.getFeatureFlag('new-plp-disable_model_switcher') === 'test') {  
 // console.log("test");
 //document.querySelector("#shopify-section-template--25172661141770__main .color-scheme").style.display = "none";
  //document.querySelector("#shopify-section-template--25172661141770__ab_test_main_collection_dAFTKV .color-scheme").style.display = "block";
//} else {
  //  console.log("control");
//}
